import {
  BookOpen,
  CheckCheck,
  ChevronDown,
  Clapperboard,
  Clock,
  Film,
  Play,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Empty, Media, Status } from '../components/common';
import { useLab } from '../lib/service';
import { MODELS, money } from '@drama/domain';
import { episodeCommandAllowance } from '../../../../packages/domain/src/production-plan';
import {} from '../shared';
import { EpisodeReview, EpisodeRevision } from './EpisodeReview';
import { useSeries } from './context';

/** Screenplay, shot list, production allowance and episode review. */
export function EpisodesTab() {
  const {
    detail: d,
    project: p,
    episode: ep,
    shots,
    plan,
    controlsDisabled,
    available,
    model,
    effectiveModel,
    providerProfile,
    setModel,
    setSelectedShot,
    setSelectedEpisode,
    narration,
    setNarration,
    run,
  } = useSeries();
  const lab = useLab();
  const budget = lab.home?.budget;
  const needsOmni =
    effectiveModel === MODELS.controlledVideo && shots.some((s) => s.spec.duration > 8);
  const commandAllowance = episodeCommandAllowance(plan.allowance, providerProfile);
  const overBudget = !lab.isDemo && (commandAllowance > available || !!budget?.suspended);
  return (
    <>
      {!ep ? (
        <Empty
          title="Every episode is a new beginning"
          text="Plan your season first. Then produce episodes one at a time."
        />
      ) : (
        <>
          <div className="episode-toolbar">
            <label>
              <span className="field-label">EPISODE</span>
              <select value={ep._id} onChange={(e) => setSelectedEpisode(e.target.value)}>
                {d.episodes.map((e) => (
                  <option key={e._id} value={e._id}>
                    {String(e.number).padStart(2, '0')} — {e.title}
                  </option>
                ))}
              </select>
            </label>
            {!ep.currentMasterId && !d.managedProviderProfile && (
              <label>
                <span className="field-label">VIDEO RECIPE</span>
                <select value={model} onChange={(e) => setModel(e.target.value)}>
                  <option value={MODELS.video}>Gemini Omni · standard</option>
                  <option
                    value={MODELS.controlledVideo}
                    disabled={shots.some((s) => s.spec.duration > 8)}
                  >
                    Veo 3.1 · controlled (up to 8 seconds)
                  </option>
                </select>
              </label>
            )}
            {!ep.currentMasterId && (
              <div className="episode-actions">
                {!ep.scriptVersion ? (
                  <Button
                    disabled={controlsDisabled || !p.castLocked}
                    onClick={() => void run('PLAN_EPISODE')}
                  >
                    <BookOpen size={16} />
                    Prepare screenplay
                  </Button>
                ) : (
                  <>
                    <Button
                      variant={plan.newShots === 0 ? undefined : 'outline'}
                      disabled={
                        controlsDisabled || plan.newShots > 0 || !!ep.currentMasterId || overBudget
                      }
                      aria-label="Assemble episode"
                      onClick={() => void run('ASSEMBLE_EPISODE')}
                    >
                      <Film size={16} />
                      {plan.newShots === 0 ? 'Rebuild episode' : 'Assemble episode'}
                    </Button>
                    {plan.newShots > 0 && (
                      <Button
                        disabled={
                          controlsDisabled || needsOmni || !!ep.currentMasterId || overBudget
                        }
                        onClick={() =>
                          void run('PRODUCE_EPISODE', {
                            assembleWhenReady: true,
                            maxCostMicros: commandAllowance,
                          })
                        }
                      >
                        <Play size={15} />
                        Produce episode
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            {needsOmni && <p>Choose Gemini Omni for this episode’s longer takes.</p>}
          </div>
          {!!ep.scriptVersion && !ep.currentMasterId && (
            <div className="production-allowance" aria-label="Production allowance">
              <strong>
                {lab.isDemo
                  ? 'Simulation · no AI charges'
                  : `${money(commandAllowance)} command spending limit`}
              </strong>
              <p>
                {plan.newShots} shots to generate · {plan.reusedShots} shots to reuse · captions and
                assembly included.
              </p>
              <p>
                {lab.isDemo
                  ? 'The live plan would reserve up to ' +
                    money(plan.allowance) +
                    ' across its operations.'
                  : `${money(available)} available within your current limits. Usage may be lower; these are estimates, not confirmed billing.`}
              </p>
              {!lab.isDemo && commandAllowance < plan.allowance && (
                <p>
                  The full estimate is {money(plan.allowance)}. This request stops at its{' '}
                  {money(commandAllowance)} limit and may pause before the episode finishes.
                </p>
              )}
              {!lab.isDemo && commandAllowance > available && (
                <p role="status">
                  This plan exceeds the remaining allowance. You can produce individual shots from
                  the board or review your budget.
                </p>
              )}
              {d.commands.at(-1)?.status === 'FAILED' && (
                <p role="status">
                  The last request stopped. Review its findings in History; passed shots will be
                  kept when you continue.
                </p>
              )}
            </div>
          )}
          <div className="episode-summary">
            <div>
              <h2>{ep.title}</h2>
              <p>{ep.synopsis}</p>
            </div>
            <Status value={ep.status} />
          </div>
          {ep.currentMasterId && <EpisodeReview key={ep._id} />}
          {!ep.currentMasterId &&
            d.commands.some(
              (command) => command.episodeId === ep._id && command.kind === 'REGENERATE_SHOT',
            ) && (
              <div className="notice" role="status">
                Review the updated shot from the board. Once its checks pass, rebuild the episode to
                review and approve the new cut. Other shots are reused.
              </div>
            )}
          {!!shots.length && (
            <div className="shot-summary">
              <span>
                <Clapperboard size={15} />
                {shots.length} shots
              </span>
              <span>
                <Clock size={15} />
                {shots.reduce((n, s) => n + s.spec.duration, 0)} seconds
              </span>
              <span>
                <CheckCheck size={15} />
                {shots.filter((s) => s.status === 'ACCEPTED').length} accepted
              </span>
              <span>Screenplay v{ep.scriptVersion}</span>
            </div>
          )}
          {(ep.scriptNotes?.doctor ||
            ep.scriptNotes?.doctorSkipped ||
            ep.scriptNotes?.review ||
            ep.review) && (
            <details className="screenplay" aria-label="Director's notes">
              <summary>
                <Sparkles size={16} />
                Director’s notes
                <ChevronDown size={15} />
              </summary>
              {ep.scriptNotes?.hook && <p>Hook: {ep.scriptNotes.hook}</p>}
              {ep.scriptNotes?.cliffhanger && <p>Ends on: {ep.scriptNotes.cliffhanger}</p>}
              {ep.scriptNotes?.doctor && (
                <>
                  <p>
                    Script doctor scored the draft{' '}
                    {Object.entries(ep.scriptNotes.doctor.scores)
                      .map(([criterion, score]) => `${criterion} ${score}`)
                      .join(' · ')}
                  </p>
                  <ul>
                    {ep.scriptNotes.doctor.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </>
              )}
              {ep.scriptNotes?.doctorSkipped && (
                <p>The draft was kept: the script doctor could not finish.</p>
              )}
              {ep.scriptNotes?.review && (
                <>
                  <p>
                    Finished screenplay:{' '}
                    {ep.scriptNotes.review.passed
                      ? 'passed its independent review'
                      : 'needs revision before filming'}
                    .
                  </p>
                  <ul>
                    {ep.scriptNotes.review.notes.map((note: string) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </>
              )}
              {ep.review && (
                <>
                  <p>
                    Review of the cut: {ep.review.summary} Hook{' '}
                    {ep.review.hookLands ? 'lands' : 'does not land'}; ending{' '}
                    {ep.review.cliffhangerLands ? 'pulls' : 'does not pull'} towards the next
                    episode.
                  </p>
                  {ep.review.remade && (
                    <p>
                      {ep.review.remade.shotKey} was filmed again
                      {ep.review.remade.restored ? ', and the first take was kept' : ''}.
                    </p>
                  )}
                  <ul>
                    {ep.review.issues.map((issue) => (
                      <li key={`${issue.shotKey}-${issue.description}`}>
                        {issue.shotKey} · {issue.severity} {issue.category}: {issue.description}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </details>
          )}
          {ep.screenplay && (
            <details className="screenplay">
              <summary>
                <BookOpen size={16} />
                Read screenplay
                <ChevronDown size={15} />
              </summary>
              <pre>{ep.screenplay}</pre>
            </details>
          )}
          <div
            className={`shot-grid ${ep.currentMasterId ? 'shot-strip' : ''}`}
            aria-label="Episode shot board"
          >
            {shots.map((s, i) => (
              <button
                key={s._id}
                className="shot-card"
                aria-label={`Review shot ${String(i + 1).padStart(2, '0')}`}
                onClick={() => setSelectedShot(s._id)}
              >
                <div className={`shot-art shot-tone-${i % 4}`}>
                  <span className="shot-number">{String(i + 1).padStart(2, '0')}</span>
                  <Media
                    placeholder={lab.isDemo ? 'Simulated shot preview' : 'Shot reference pending'}
                    assetId={
                      lab.isDemo
                        ? undefined
                        : d.versions.find(
                            (version) =>
                              version.shotId === s._id && version.version === s.currentVersion,
                          )?.referenceAssetId
                    }
                  />
                  <span className="shot-time">{s.spec.duration}s</span>
                  <span className="shot-play">
                    <Play size={19} />
                  </span>
                </div>
                <div className="shot-body">
                  <span className="shot-framing">{s.spec.framing}</span>
                  <h3>{s.spec.action.split('.')[0]}</h3>
                  <div>
                    <Status value={s.status} />
                    <span className="version-tag">v{s.currentVersion}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {!!ep.scriptVersion && !ep.currentMasterId && <EpisodeRevision />}
          {!!ep.scriptVersion && (
            <details className="screenplay">
              <summary>Optional opening narration</summary>
              <p className="muted">
                An offscreen line in the first six seconds. Review against the opening shot before
                accepting.
              </p>
              <textarea
                aria-label="Opening narration"
                maxLength={120}
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                placeholder="Some doors are better left unopened."
              />
              <Button
                variant="outline"
                disabled={controlsDisabled || !narration.trim()}
                onClick={() => void run('ADD_NARRATION', { instruction: narration })}
              >
                Generate narration
              </Button>
              {ep.narrationAssetId && <p>Narration saved. Reassemble to hear it in the episode.</p>}
            </details>
          )}
          {ep.acceptedMasterId && ep.acceptedMasterId !== ep.currentMasterId && (
            <details className="screenplay">
              <summary>Earlier accepted episode</summary>
              <p>Your previous approved version remains available while you revise.</p>
              <Media assetId={ep.acceptedMasterId} video />
            </details>
          )}
        </>
      )}
    </>
  );
}
